import React, { useState } from 'react';
import { FaCalendarAlt, FaClock, FaMapMarkerAlt, FaInfoCircle, FaTimes, FaCheck, FaUser, FaPhone } from 'react-icons/fa';
import './booking-modal.css';
import { createBooking } from '../../api/user/serviceProvider/service-booking';
import Payment from '../payments/Payment';

const CONTACT_NUMBER_MAX_LENGTH = 9;

const BookingModal = ({ offer, showBookModal }) => {
  const [activeTab, setActiveTab] = useState('date');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [timeSlot, setTimeSlot] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showPayment, setShowPayment] = useState(false);
  const [pendingBookingData, setPendingBookingData] = useState(null);
  const [countryCode, setCountryCode] = useState('+237');

  const [bookingDetails, setBookingDetails] = useState({
    clientName: '',
    location: '',
    description: '',
    contactNumber: '',
    specialRequests: ''
  });

  if (!offer || !offer._id) {
    return (
      <div className="booking-modal-overlay">
        <div className="booking-modal-container booking-error-state">
          <button className="close-btn" onClick={showBookModal} aria-label="Close">
            <FaTimes />
          </button>
          <div className="booking-error-block">
            <FaInfoCircle />
            <p>Offer information is not available.</p>
            <button onClick={showBookModal} className="retry-btn">Close</button>
          </div>
        </div>
      </div>
    );
  }

  const isProviderActive = offer.userService?.isActive ?? false;
  if (!isProviderActive) {
    return (
      <div className="booking-modal-overlay">
        <div className="booking-modal-container booking-error-state">
          <button className="close-btn" onClick={showBookModal} aria-label="Close">
            <FaTimes />
          </button>
          <div className="booking-error-block">
            <FaInfoCircle />
            <p>This provider is currently inactive and not accepting bookings right now.</p>
            <button onClick={showBookModal} className="retry-btn">Close</button>
          </div>
        </div>
      </div>
    );
  }

  const timeSlots = [];
  for (let hour = 8; hour <= 20; hour++) {
    timeSlots.push(`${hour}:00`);
  }

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setActiveTab('time');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBookingDetails(prev => ({ ...prev, [name]: value }));
  };

  const handleCountryCodeChange = (e) => {
    let value = e.target.value.replace(/[^\d+]/g, '');
    if (value.indexOf('+') > 0) {
      value = '+' + value.replace(/\+/g, '');
    }
    setCountryCode(value.slice(0, 5));
  };

  const handleContactNumberChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, CONTACT_NUMBER_MAX_LENGTH);
    setBookingDetails(prev => ({ ...prev, contactNumber: digitsOnly }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!offer.userService?.isActive) {
      setError('This provider is no longer active and cannot accept bookings.');
      return;
    }

    const validationErrors = [];
    if (!timeSlot) validationErrors.push('Please select a time slot');
    if (!bookingDetails.location) validationErrors.push('Location is required');
    if (!bookingDetails.description) validationErrors.push('Description is required');
    if (!countryCode) validationErrors.push('Country code is required');
    if (!bookingDetails.contactNumber) validationErrors.push('Contact number is required');
    else if (bookingDetails.contactNumber.length < 8) validationErrors.push('Contact number must be at least 8 digits');

    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    const formattedDate = selectedDate.toISOString().split('T')[0];
    const pendingData = {
      offerId: offer._id,
      date: formattedDate,
      timeSlot,
      clientName: bookingDetails.clientName,
      location: bookingDetails.location,
      description: bookingDetails.description,
      contactNumber: `${countryCode}${bookingDetails.contactNumber}`,
      specialRequests: bookingDetails.specialRequests || '',
    };
    setPendingBookingData(pendingData);
    setError('');
    // setShowPayment(true);
    
    // TEMPORARY BYPASS: call payment submit directly
    handlePaymentSubmit('0000', 'none', pendingData);
  };

  const handlePaymentSubmit = async (number, medium, customPendingData) => {
    if (!offer.userService?.isActive) {
      setShowPayment(false);
      setError('This provider is no longer active and cannot accept bookings.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      const dataToSubmit = customPendingData || pendingBookingData;
      const response = await createBooking({
        ...dataToSubmit,
        number,
        medium,
      });

      if (response.success) {
        setShowPayment(false);
        setSubmitSuccess(true);
      } else if (response.timedOut) {
        // Payment wasn't confirmed as failed OR successful in time.
        // The backend reconciliation job will finalize it automatically.
        setShowPayment(false);
        setSubmitSuccess(false);
        setError(
          "We're still confirming your payment — this can take a few minutes. " +
          "You don't need to pay again. We'll finalize your booking automatically once it's confirmed." +
          (response.bookingId ? ` Reference: ${response.bookingId}` : '')
        );
      } else {
        throw new Error(response.error || 'Booking failed');
      }
    } catch (err) {
      setShowPayment(false);
      setError(err.message || 'Payment or booking failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCalendarDays = () => {
    const days = [];
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`prev-${i}`} className="calendar-day empty"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(currentYear, currentMonth, day);
      const isSelected = currentDate.toDateString() === selectedDate.toDateString();
      const isToday = currentDate.toDateString() === new Date().toDateString();
      const isPast = currentDate < new Date() && !isToday;

      days.push(
        <button
          type="button"
          key={`day-${day}`}
          className={`calendar-day 
            ${isSelected ? 'selected' : ''} 
            ${isToday ? 'today' : ''}
            ${isPast ? 'past' : ''}`}
          onClick={() => !isPast && handleDateSelect(currentDate)}
          disabled={isPast}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  return (
    <div className="booking-modal-overlay">
      <div className="booking-modal-container">
        <button className="close-btn" onClick={showBookModal} aria-label="Close">
          <FaTimes />
        </button>

        {isSubmitting ? (
          <div className="booking-success">
            <div className="success-icon" style={{ borderColor: '#007bff' }}>
              <div className="spinner" style={{ borderTopColor: '#007bff' }}></div>
            </div>
            <h3>Processing payment…</h3>
            <p style={{ color: '#666', textAlign: 'center' }}>
              Please confirm the payment request on your phone.<br />
              This may take up to 2 minutes.
            </p>
          </div>
        ) : submitSuccess ? (
          <div className="booking-success">
            <div className="success-icon">
              <FaCheck />
            </div>
            <h3>Booking confirmed</h3>
            <div className="booking-summary">
              <p><strong>Service:</strong> {offer.title}</p>
              <p><strong>Provider:</strong> {offer.userService?.name}</p>
              <p><FaCalendarAlt /> {selectedDate.toDateString()}</p>
              <p><FaClock /> {timeSlot}</p>
              <p><FaMapMarkerAlt /> {bookingDetails.location}</p>
              <p><FaUser /> {countryCode}{bookingDetails.contactNumber}</p>
              <p className="summary-price"><strong>Price:</strong> XAF {offer.price?.toLocaleString()}</p>
            </div>
            <button className="btn-primary" onClick={showBookModal}>Done</button>
          </div>
        ) : (
          <>
            <div className="booking-header">
              <div className="booking-header-top">
                <p className="booking-eyebrow">Book service</p>
                <h2>{offer.title}</h2>
              </div>

              <div className="provider-row">
                <div className="provider-avatar">
                  {(offer.userService?.name || 'NA').slice(0, 2).toUpperCase()}
                </div>
                <div className="provider-meta">
                  <p className="provider-name">{offer.userService?.name || 'Provider'}</p>
                  <p className="provider-type">{offer.service?.name}</p>
                </div>
                <p className="provider-price">XAF {offer.price?.toLocaleString()}</p>
              </div>

              <div className="booking-warning">
                <FaInfoCircle />
                <span>A {(!isNaN(Number(offer.service?.fee)) && Number(offer.service?.fee) > 0) ? offer.service.fee : 0} FCFA platform fee applies after booking. You'll get the provider's contact once confirmed.</span>
              </div>

              {error && (
                <div className="booking-error">
                  <FaInfoCircle /> {error}
                </div>
              )}
            </div>

            <div className="booking-content">
              <div className="booking-left">
                <div className="tabs">
                  <button
                    type="button"
                    className={`tab ${activeTab === 'date' ? 'active' : ''}`}
                    onClick={() => setActiveTab('date')}
                  >
                    <FaCalendarAlt /> Date
                  </button>
                  <button
                    type="button"
                    className={`tab ${activeTab === 'time' ? 'active' : ''}`}
                    onClick={() => setActiveTab('time')}
                  >
                    <FaClock /> Time
                  </button>
                </div>

                {activeTab === 'date' ? (
                  <div className="calendar-container">
                    <div className="calendar-header">
                      <button
                        type="button"
                        className="nav-btn"
                        onClick={() => {
                          if (currentMonth === 0) {
                            setCurrentMonth(11);
                            setCurrentYear(currentYear - 1);
                          } else {
                            setCurrentMonth(currentMonth - 1);
                          }
                        }}
                      >
                        &lt;
                      </button>
                      <h3>
                        {new Date(currentYear, currentMonth).toLocaleString('default', { month: 'long' })} {currentYear}
                      </h3>
                      <button
                        type="button"
                        className="nav-btn"
                        onClick={() => {
                          if (currentMonth === 11) {
                            setCurrentMonth(0);
                            setCurrentYear(currentYear + 1);
                          } else {
                            setCurrentMonth(currentMonth + 1);
                          }
                        }}
                      >
                        &gt;
                      </button>
                    </div>

                    <div className="weekdays">
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                        <div key={`${day}-${i}`} className="weekday">{day}</div>
                      ))}
                    </div>

                    <div className="calendar-days">
                      {renderCalendarDays()}
                    </div>
                  </div>
                ) : (
                  <div className="time-slots-container">
                    <div className="time-slots-grid">
                      {timeSlots.map(slot => (
                        <button
                          type="button"
                          key={slot}
                          className={`time-slot-btn ${timeSlot === slot ? 'selected' : ''}`}
                          onClick={() => setTimeSlot(slot)}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="selected-info">
                  {selectedDate && (
                    <div className="info-item">
                      <FaCalendarAlt />
                      <span>{selectedDate.toDateString()}</span>
                    </div>
                  )}
                  {timeSlot && (
                    <div className="info-item">
                      <FaClock />
                      <span>{timeSlot}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="booking-right">
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label><FaUser /> Your name <span className="required-star">*</span></label>
                    <input
                      type="text"
                      name="clientName"
                      value={bookingDetails.clientName}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label><FaMapMarkerAlt /> Service location <span className="required-star">*</span></label>
                    <input
                      type="text"
                      name="location"
                      value={bookingDetails.location}
                      onChange={handleInputChange}
                      placeholder="Where should we provide the service?"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label><FaInfoCircle /> Service description <span className="required-star">*</span></label>
                    <textarea
                      name="description"
                      value={bookingDetails.description}
                      onChange={handleInputChange}
                      placeholder="Describe what you need..."
                      rows="3"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label><FaPhone /> Contact number <span className="required-star">*</span></label>
                    <div className="phone-input-group">
                      <input
                        type="text"
                        className="country-code-input"
                        value={countryCode}
                        onChange={handleCountryCodeChange}
                        placeholder="+237"
                        maxLength={5}
                        aria-label="Country code"
                        required
                      />
                      <span className="phone-divider" />
                      <input
                        type="text"
                        inputMode="numeric"
                        className="phone-number-input"
                        name="contactNumber"
                        value={bookingDetails.contactNumber}
                        onChange={handleContactNumberChange}
                        placeholder="6XXXXXXXX"
                        maxLength={CONTACT_NUMBER_MAX_LENGTH}
                        required
                      />
                    </div>
                    <span className="char-count">Country code + max {CONTACT_NUMBER_MAX_LENGTH} digits</span>
                  </div>

                  <div className="form-group">
                    <label>Special requests <span className="optional-tag">(optional)</span></label>
                    <textarea
                      name="specialRequests"
                      value={bookingDetails.specialRequests}
                      onChange={handleInputChange}
                      placeholder="Any special instructions..."
                      rows="2"
                    />
                  </div>

                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Processing...' : 'Confirm Booking'}
                  </button>
                </form>
              </div>
            </div>
          </>
        )}
      </div>

      {showPayment && (
        <Payment
          onClose={() => setShowPayment(false)}
          handleSubmit={handlePaymentSubmit}
          amounts={(!isNaN(Number(offer.service?.fee)) && Number(offer.service?.fee) > 0) ? Number(offer.service?.fee) : 500}
        />
      )}
    </div>
  );
};

export default BookingModal;